import { DateTime } from 'luxon';

import { getComparisonKey } from './serialization';
import { reduceToDismissDateAndIndexByKey } from './utils';

import type { StoredDismissedAlert } from './types';
import type { EntryFromSkeleton } from '@tuic/contentful';
import type { TypeAlertSkeleton } from '@tuic/contentful/generated/dashboard';

/**
 * Creates a filter function that checks if the provided entry is dismissed based on the dismissed alerts.
 */
export const createServerAlertFilter = (args: {
  origin: string;
  dismissedAlerts: StoredDismissedAlert[];
}) => {
  const { origin, dismissedAlerts } = args;

  // Group dismissed alerts by their dismissed date
  const groupedByDate = reduceToDismissDateAndIndexByKey(dismissedAlerts);

  return (entry: EntryFromSkeleton<TypeAlertSkeleton>): boolean => {
    // Create the comparison key for the entry
    const key = getComparisonKey({
      id: entry.sys.id,
      origin,
    });

    const wasNotDismissed = !(key in groupedByDate);

    if (typeof entry.fields.resetAt === 'undefined') {
      return wasNotDismissed;
    }

    const resetAt = DateTime.fromISO(entry.fields.resetAt).toLocal();

    if (!resetAt.isValid) {
      return wasNotDismissed;
    }

    return shouldBeRendered(resetAt, groupedByDate[key]);
  };
};

/**
 * Creates a filter function that checks if the provided alert is dismissed based on the collection of reset dates.
 */
export const createClientAlertFilter = (args: {
  resetCollection: Record<string, string>;
}) => {
  const { resetCollection } = args;

  return (clientDismissedAlert: StoredDismissedAlert): boolean => {
    const wasResettet = clientDismissedAlert.id in resetCollection;

    if (!wasResettet) {
      return false;
    }

    const resetAt = DateTime.fromISO(
      resetCollection[clientDismissedAlert.id],
    ).toLocal();

    if (!resetAt.isValid) {
      return false;
    }

    return shouldBeRendered(resetAt, clientDismissedAlert.dismissedAt);
  };
};

/**
 * Indicates whether the alert should be rendered based on the reset and dismissed dates.
 */
const shouldBeRendered = (
  resetAt: DateTime<true> | undefined,
  dismissedAt: DateTime<true> | undefined,
): boolean => {
  // If no dismissedAt date is provided, the alert should be rendered
  if (typeof dismissedAt === 'undefined') {
    return true;
  }

  // If no resetAt date is provided, the alert should not be rendered, because it is not reset and has set a dismissed date.
  if (typeof resetAt === 'undefined') {
    return false;
  }

  const now = DateTime.now();

  // If the reset date is in the future, the alert should not be rendered -- future reset date would lead to alerts which cannot be dismissed.
  if (resetAt > now) {
    return false;
  }

  // If the dismissed date is before the reset date, the alert should be rendered.
  return dismissedAt < resetAt;
};
